import { useState } from 'react'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useWasteCategories,
  useCreateWasteCategory,
  useUpdateWasteCategory,
  useDeleteWasteCategory,
} from '@/hooks/queries/useWasteCategories'
import { WasteTypesToolbar } from '@/pages/waste-types/components/WasteTypesToolbar'
import type { WasteCategory } from '@/types'

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; category: WasteCategory }
  | { mode: 'delete'; category: WasteCategory }

function getServerError(error: unknown): string | null {
  if (!error) return null
  const err = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
  const errors = err.response?.data?.errors
  if (errors) return Object.values(errors).flat().join(' ')
  return err.response?.data?.message ?? 'Ocurrió un error inesperado'
}

export function WasteCategoriesTab() {
  const { data: categories, isLoading } = useWasteCategories()
  const createMutation = useCreateWasteCategory()
  const updateMutation = useUpdateWasteCategory()
  const deleteMutation = useDeleteWasteCategory()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' })
  const [nameInput, setNameInput] = useState('')
  const [search, setSearch] = useState('')

  const openCreate = () => {
    setNameInput('')
    createMutation.reset()
    setDialog({ mode: 'create' })
  }

  const openEdit = (category: WasteCategory) => {
    setNameInput(category.name)
    updateMutation.reset()
    setDialog({ mode: 'edit', category })
  }

  const openDelete = (category: WasteCategory) => {
    deleteMutation.reset()
    setDialog({ mode: 'delete', category })
  }

  const closeDialog = () => setDialog({ mode: 'closed' })

  const handleSubmit = async () => {
    if (dialog.mode === 'create') {
      await createMutation.mutateAsync({ name: nameInput })
      closeDialog()
    } else if (dialog.mode === 'edit') {
      await updateMutation.mutateAsync({ id: dialog.category.id, data: { name: nameInput } })
      closeDialog()
    }
  }

  const handleDelete = async () => {
    if (dialog.mode !== 'delete') return
    await deleteMutation.mutateAsync(dialog.category.id)
    closeDialog()
  }

  const isFormDialog = dialog.mode === 'create' || dialog.mode === 'edit'
  const activeMutation = dialog.mode === 'create' ? createMutation : updateMutation
  const formError = isFormDialog ? getServerError(activeMutation.error) : null
  const deleteError = dialog.mode === 'delete' ? getServerError(deleteMutation.error) : null

  const normalizedSearch = search.trim().toLowerCase()
  const filtered = (categories ?? []).filter((c) =>
    !normalizedSearch || c.name.toLowerCase().includes(normalizedSearch)
  )

  return (
    <>
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-between">
          <WasteTypesToolbar
            search={search}
            onSearchChange={setSearch}
            onClearFilters={() => setSearch('')}
            hasActiveFilters={Boolean(search.trim())}
          />
          <Button onClick={openCreate} className="ml-4 shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Nueva categoría
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : !categories?.length ? (
          <p className="text-sm text-muted-foreground">No hay categorías registradas.</p>
        ) : !filtered.length ? (
          <p className="text-sm text-muted-foreground">No se encontraron categorías para esa búsqueda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                <th className="pb-2">Nombre</th>
                <th className="pb-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cat) => (
                <tr key={cat.id} className="border-b last:border-0">
                  <td className="py-3 font-medium text-foreground">{cat.name}</td>
                  <td className="py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mr-1 h-8 w-8"
                      onClick={() => openEdit(cat)}
                      aria-label={`Editar ${cat.name}`}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openDelete(cat)}
                      aria-label={`Eliminar ${cat.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={isFormDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Nueva categoría' : 'Editar categoría'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="category-name">Nombre</Label>
              <Input
                id="category-name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                autoFocus
              />
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!nameInput.trim() || activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog.mode === 'delete'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar categoría</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70">
            ¿Estás seguro que deseas eliminar{' '}
            <span className="font-semibold">
              {dialog.mode === 'delete' ? dialog.category.name : ''}
            </span>
            ? Esta acción no se puede deshacer.
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
